import React, { useCallback, useMemo } from 'react';
import { InvoiceForClientFragment, StripePaymentIntentStatusFragment } from 'shared/types/graphql';
import { getInvoiceIdentifierCopy, getMonetaryDisplayValue, getUserTimezoneAbbreviation } from 'shared/utils';
import { format } from 'date-fns';
import remove from 'lodash-es/remove';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Flex, Skeleton } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';

interface InvoiceSelectorProps {
  invoice?: InvoiceForClientFragment;
  selectedInvoiceIds: string[];
  isPendingPaymentIntents: (intents: StripePaymentIntentStatusFragment[]) => boolean;
  setSelectedInvoiceIds: React.Dispatch<React.SetStateAction<string[]>>;
  showSkeleton?: boolean;
}

const InvoiceSelector = ({
  invoice,
  selectedInvoiceIds,
  isPendingPaymentIntents,
  setSelectedInvoiceIds,
  showSkeleton,
}: InvoiceSelectorProps): JSX.Element => {
  const isSelected = useMemo(
    () => !!invoice?.id && selectedInvoiceIds.includes(invoice.id),
    [invoice?.id, selectedInvoiceIds],
  );

  const handleInvoiceSelected = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!invoice?.id || showSkeleton) return;

      event.preventDefault();

      if (!isSelected) {
        setSelectedInvoiceIds((currentSelection) => [...currentSelection, invoice.id]);
      } else {
        setSelectedInvoiceIds((currentSelection) => remove(currentSelection, (id) => id !== invoice.id));
      }
    },
    [invoice?.id, isSelected, showSkeleton, setSelectedInvoiceIds],
  );

  const invoiceLabel = useMemo(
    () => `Invoice ${getInvoiceIdentifierCopy(invoice?.identifier, invoice?.date)}`,
    [invoice?.date, invoice?.identifier],
  );

  const invoiceDetails = useMemo(
    () =>
      `Updated: ${format(
        new Date(invoice?.date || null),
        `MM/dd/yy '·' h:mmaaaaa'm' '${getUserTimezoneAbbreviation()}'`,
      )}`,
    [invoice?.date],
  );

  return (
    <Flex
      key={invoice?.id}
      justifyContent="space-between"
      cursor="pointer"
      my={3}
      p={2}
      gap={2}
      borderRadius="md"
      borderWidth="2px"
      borderColor={isSelected ? 'border.focus' : 'border.default'}
      onClick={handleInvoiceSelected}
    >
      <Flex direction="column" gap={showSkeleton ? 1 : 0}>
        <Skeleton isLoaded={!showSkeleton} display="flex">
          <Text fontSize="sm" fontWeight="semibold">
            {invoiceLabel}
          </Text>
        </Skeleton>
        <Skeleton isLoaded={!showSkeleton} display="flex">
          <Text fontSize="xs">{invoiceDetails}</Text>
        </Skeleton>
      </Flex>
      <Flex direction="column" alignItems="flex-end">
        <Flex alignItems="center" gap={2}>
          <Skeleton isLoaded={!showSkeleton}>
            <Text data-testid="discovered-invoice-amount" fontWeight="semibold" fontSize="lg">
              {getMonetaryDisplayValue(invoice?.total, true)}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!showSkeleton}>
            <Checkbox isChecked={isSelected} />
          </Skeleton>
        </Flex>
        {invoice?.stripePaymentIntents && isPendingPaymentIntents(invoice.stripePaymentIntents) && (
          <Tag size="sm" label="Pending" backgroundColor="background.warning" color="text.warning" mt={1} mr={2} />
        )}
      </Flex>
    </Flex>
  );
};

export default InvoiceSelector;
